import { initializeApp } from 'firebase/app'
import { Messaging, MessagePayload, getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging'

import * as api from 'api/api'

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || '',
  appId: process.env.REACT_APP_FIREBASE_APP_ID || '',
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '',
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '',
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || '',
}
const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY || undefined
const WORKER_URL = '/firebase-messaging-sw.js'
const TOKEN_KEY = 'jopath.pushToken'

export type PushStatus = 'unsupported' | NotificationPermission

let messagingPromise: Promise<Messaging | null> | null = null

const loadMessaging = (): Promise<Messaging | null> => {
  if (!messagingPromise) {
    messagingPromise = (async () => {
      const { apiKey, appId, messagingSenderId, projectId } = firebaseConfig
      if (!apiKey || !appId || !messagingSenderId || !projectId) return null
      if (!(await isSupported().catch(() => false))) return null
      return getMessaging(initializeApp(firebaseConfig))
    })()
  }
  return messagingPromise
}

export const pushStatus = async (): Promise<PushStatus> =>
  (await loadMessaging()) ? Notification.permission : 'unsupported'

export const hasPushToken = () => Boolean(localStorage.getItem(TOKEN_KEY))

/** The first call must come from a click: Safari and Firefox only show the permission prompt during a user gesture. */
export const enablePush = async (): Promise<boolean> => {
  if (typeof Notification === 'undefined') return false
  const permission =
    Notification.permission === 'default' ? await Notification.requestPermission() : Notification.permission
  if (permission !== 'granted') return false
  const messaging = await loadMessaging()
  if (!messaging) return false
  const serviceWorkerRegistration = await navigator.serviceWorker.register(
    `${WORKER_URL}?${new URLSearchParams(firebaseConfig)}`
  )
  const token = await getToken(messaging, { serviceWorkerRegistration, vapidKey: VAPID_KEY })
  await api.registerPushToken(token)
  localStorage.setItem(TOKEN_KEY, token)
  return true
}

/** Must run before the session token is cleared, because the request reads the Authorization header when it starts. */
export const disablePush = () => {
  const token = localStorage.getItem(TOKEN_KEY)
  if (!token) return
  localStorage.removeItem(TOKEN_KEY)
  api.unregisterPushToken(token).catch(() => undefined)
}

export const onPushMessage = (listener: (payload: MessagePayload) => void): (() => void) => {
  let active = true
  let unsubscribe: () => void = () => undefined
  loadMessaging().then((messaging) => {
    if (messaging && active) unsubscribe = onMessage(messaging, listener)
  })
  return () => {
    active = false
    unsubscribe()
  }
}
